// Die Namen auf der Karte: Laender, Orte, Meere. Sie liegen in einer
// eigenen Leaflet-Ebene UEBER den Kreisflaechen - genau wie in der Vorlage
// (docs/reference/IMG_4900.png), wo "Hannover" auch auf einem rot
// gefuellten Kreis noch lesbar ist. Laegen sie darunter, waere von den
// Namen in Deutschland nichts mehr zu sehen.

import { useMemo, useState } from "react";
import { divIcon } from "leaflet";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import {
  ohneUeberdeckung,
  sichtbareBeschriftungen,
  type Basiskarte,
  type Beschriftung,
  type Namenskasten,
} from "../lib/basiskarte.js";

// Deckel je Art. Sie greifen nur, wenn weit herausgezoomt wird; jede
// Beschriftung ist ein eigenes DOM-Element, und ab ein paar hundert
// ruckelt das Schieben der Karte spuerbar.
const HOECHSTENS_ORTE = 140;
const HOECHSTENS_LAENDER = 40;
const HOECHSTENS_MEERE = 12;

/**
 * Namen kommen aus Natural Earth, also aus einer Datei im eigenen Build -
 * trotzdem maskiert, weil sie hier als HTML in ein divIcon gehen und
 * Leaflet dafuer keine sichere Fassung anbietet.
 */
function maskiert(wert: string): string {
  return wert.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Laendernamen nach Gewicht: Natural Earth laesst grosse Laender frueh
 * erscheinen (Deutschland ab 1,7), kleine spaet (Liechtenstein ab 5,5).
 * Dieselbe Zahl taugt deshalb als Schriftgroesse - sonst steht
 * LIECHTENSTEIN genauso breit auf der Karte wie DEUTSCHLAND.
 */
function grad(beschriftung: Beschriftung): string {
  if (beschriftung.abZoom <= 3) return " karte-name-land-gross";
  if (beschriftung.abZoom <= 5) return "";
  return " karte-name-land-klein";
}

/**
 * Wie breit und hoch ein Name auf dem Bildschirm wird. Geschaetzt statt
 * gemessen: echtes Messen hiesse, jeden Namen erst in den Baum zu haengen
 * und dann wieder herauszunehmen, und das fuer bis zu 190 Namen bei jedem
 * Schieben der Karte. Die Faktoren stammen aus karte.css (Schriftgroesse
 * mal mittlere Zeichenbreite der Systemschrift, rund 0,58).
 */
function ausmass(beschriftung: Beschriftung, art: "land" | "ort" | "meer"): { breite: number; hoehe: number } {
  if (art === "land") {
    const groesse = beschriftung.abZoom <= 3 ? 13 : beschriftung.abZoom <= 5 ? 12 : 10;
    // Versalien und Sperrung machen den Namen breiter als die reine
    // Schriftgroesse vermuten laesst.
    return { breite: beschriftung.name.length * groesse * 0.72, hoehe: groesse + 4 };
  }
  if (art === "meer") return { breite: beschriftung.name.length * 12 * 0.52, hoehe: 16 };
  // Ort: Punkt und Abstand kommen zum Namen dazu.
  return { breite: beschriftung.name.length * 11 * 0.56 + 11, hoehe: 15 };
}

function symbol(beschriftung: Beschriftung, art: "land" | "ort" | "meer") {
  const punkt =
    art === "ort"
      ? `<i class="${beschriftung.hauptstadt ? "karte-hauptstadt" : "karte-punkt"}"></i>`
      : "";
  return divIcon({
    className: `karte-name karte-name-${art}${art === "land" ? grad(beschriftung) : ""}`,
    html: `<span>${punkt}${maskiert(beschriftung.name)}</span>`,
    iconSize: [0, 0],
  });
}

export function KartenBeschriftung({ basiskarte }: { basiskarte: Basiskarte }) {
  const karte = useMap();
  const [stand, setStand] = useState(() => ({ zoom: karte.getZoom(), grenzen: karte.getBounds() }));
  useMapEvents({
    moveend: () => setStand({ zoom: karte.getZoom(), grenzen: karte.getBounds() }),
    zoomend: () => setStand({ zoom: karte.getZoom(), grenzen: karte.getBounds() }),
  });

  const sichtbar = useMemo(() => {
    // Etwas ueber den Rand hinaus: sonst fehlt beim Schieben der Name der
    // Stadt, die gerade erst hereinkommt.
    const rand = stand.grenzen.pad(0.15);
    const drin = (beschriftung: Beschriftung) => rand.contains([beschriftung.lat, beschriftung.lng]);
    const laender = sichtbareBeschriftungen(basiskarte.laenderNamen, stand.zoom, drin, HOECHSTENS_LAENDER);
    const orte = sichtbareBeschriftungen(basiskarte.orte, stand.zoom, drin, HOECHSTENS_ORTE);
    const meere = sichtbareBeschriftungen(basiskarte.meere, stand.zoom, drin, HOECHSTENS_MEERE);

    // Reihenfolge ist Rangfolge: Laender geben den Ueberblick, Staedte
    // sind das, wonach beim Sammeln gesucht wird, ein Meeresname ist
    // Beiwerk. Wer kollidiert, faellt in dieser Reihenfolge weg.
    const kaesten: Namenskasten[] = [];
    const kasten = (beschriftung: Beschriftung, art: "land" | "ort" | "meer") => {
      const punkt = karte.latLngToContainerPoint([beschriftung.lat, beschriftung.lng]);
      const { breite, hoehe } = ausmass(beschriftung, art);
      // Der Ortsname haengt rechts am Punkt (karte.css), Laender- und
      // Meeresnamen stehen mittig darueber.
      kaesten.push({
        schluessel: `${art}-${beschriftung.name}-${beschriftung.lng}`,
        x: art === "ort" ? punkt.x - 4 : punkt.x - breite / 2,
        y: punkt.y - hoehe / 2,
        breite,
        hoehe,
      });
    };
    for (const beschriftung of laender) kasten(beschriftung, "land");
    for (const beschriftung of orte) kasten(beschriftung, "ort");
    for (const beschriftung of meere) kasten(beschriftung, "meer");
    const frei = ohneUeberdeckung(kaesten);
    const passt = (art: string) => (beschriftung: Beschriftung) =>
      frei.has(`${art}-${beschriftung.name}-${beschriftung.lng}`);

    return {
      laender: laender.filter(passt("land")),
      orte: orte.filter(passt("ort")),
      meere: meere.filter(passt("meer")),
    };
  }, [basiskarte, stand, karte]);

  return (
    <>
      {sichtbar.meere.map((meer) => (
        <Marker
          key={`meer-${meer.name}-${meer.lng}`}
          position={[meer.lat, meer.lng]}
          icon={symbol(meer, "meer")}
          interactive={false}
          pane="beschriftung"
        />
      ))}
      {sichtbar.laender.map((land) => (
        <Marker
          key={`land-${land.name}-${land.lng}`}
          position={[land.lat, land.lng]}
          icon={symbol(land, "land")}
          interactive={false}
          pane="beschriftung"
        />
      ))}
      {sichtbar.orte.map((ort) => (
        <Marker
          key={`ort-${ort.name}-${ort.lng}`}
          position={[ort.lat, ort.lng]}
          icon={symbol(ort, "ort")}
          interactive={false}
          pane="beschriftung"
        />
      ))}
    </>
  );
}
